import RequestBuilder, { comparisonTypes as ComparisonTypes } from 'Resources/RequestBuilder';
import _ from 'lodash';
import parse from './filterParse';
import { ListViewFilter } from 'App/resources/AllIWant';
import { CustomFieldFilterConfig } from 'App/babel/filterConfigs/FilterConfig';
import AppService from 'App/legacy/AppService';
// import { ComparisonType } from 'Resources/ComparisonTypes';

const notCTToCT = {
	[ComparisonTypes.NotEquals]: ComparisonTypes.Equals,
	[ComparisonTypes.NotGreaterThan]: ComparisonTypes.GreaterThan,
	[ComparisonTypes.NotGreaterThanEquals]: ComparisonTypes.GreaterThanEquals,
	[ComparisonTypes.NotLessThan]: ComparisonTypes.LessThan, // same as ComparisonTypes.NotLowerThan
	[ComparisonTypes.NotLessThanEquals]: ComparisonTypes.LessThanEquals, // same as ComparisonTypes.NotLowerThanEquals
	[ComparisonTypes.NotBetween]: ComparisonTypes.Between,
	[ComparisonTypes.NotTerm]: ComparisonTypes.Term,
	[ComparisonTypes.NotSearch]: ComparisonTypes.Search,
	[ComparisonTypes.NotMatchPhrase]: ComparisonTypes.MatchPhrase,
	[ComparisonTypes.NotMatch]: ComparisonTypes.Match,
	[ComparisonTypes.NotWildcard]: ComparisonTypes.Wildcard,
	[ComparisonTypes.NotWildcardEnd]: ComparisonTypes.WildcardEnd,
	[ComparisonTypes.NotWildcardStart]: ComparisonTypes.WildcardStart,
	[ComparisonTypes.NotMatchExact]: ComparisonTypes.MatchExact
} as const;

export const customDatatypeFields = {
	Integer: 'valueInteger',
	Percent: 'valueInteger',
	Currency: 'valueDouble',
	Discount: 'valueDouble',
	Double: 'valueDouble',
	Boolean: 'valueBoolean',
	Checkbox: 'valueBoolean',
	Date: 'valueDate',
	Time: 'valueTime',
	Text: 'value',
	String: 'value',
	Email: 'value',
	Link: 'value',
	Select: 'value',
	MultiSelect: 'valueArray',
	User: 'value',
	Users: 'valueArray',
	Calculation: 'valueInteger',
	Phone: 'value'
} as const;

const buildCustomFilter = (
	filter: ListViewFilter,
	filterName: string,
	filterConfig: CustomFieldFilterConfig,
	type: string,
	requestBuilder: RequestBuilder,
	prefix: string,
	options: { useTags?: boolean; preserveDatePresets?: boolean }
) => {
	if (!filter.inactive) {
		var groupBuilder = requestBuilder.groupBuilder();
		var filterWasAdded = false;
		// Nothing picked is no filter; the backends read an empty list as matching nothing.
		const isEmptySelection = Array.isArray(filter.value) && !filter.value.length;
		var ct;
		var tempPrefix = prefix;
		if (filterName.indexOf('.') !== -1) {
			var customType = filterName.substring(0, filterName.lastIndexOf('.'));
			let filterType = _.get(options, 'filterType', type);
			if (filterType === 'userDefinedObject') {
				filterType = 'userDefinedObject1';
			}
			if (customType !== filterType) {
				if (customType === 'account') {
					customType = 'client';
				}
				tempPrefix = customType + '.';
			}
		}

		if (
			filterConfig.$field &&
			customDatatypeFields[filterConfig.$field.datatype as keyof typeof customDatatypeFields]
		) {
			tempPrefix = filterName.indexOf('CustomAgreement') !== -1 ? 'metadata.' : tempPrefix;

			var valueField =
				tempPrefix +
				'custom.' +
				customDatatypeFields[filterConfig.$field.datatype as keyof typeof customDatatypeFields];
			switch (filterConfig.$field.datatype) {
				case 'Discount':
				case 'Currency':
				case 'Percent':
				case 'Time':
					filterWasAdded = parse.range(
						groupBuilder,
						valueField,
						filter.value as { start: string; end: string }
					);
					break;

				case 'Integer':
				case 'Calculation':
					filterWasAdded = parse.numRange(
						groupBuilder,
						valueField,
						filter.value as { start: string; end: string }
					);
					break;

				case 'Date': {
					const dateValue = filter.value as { start: string; end: string; preset: string };
					// For hasValue/hasNoValue, we check if the custom field record exists
					if (dateValue.preset === 'hasValue') {
						// Has a value = custom field record exists with this fieldId
						// The groupBuilder already creates a group, we just need to add fieldId filter
						// which happens below in filterWasAdded block
						filterWasAdded = true;
					} else if (dateValue.preset === 'hasNoValue') {
						// Has no value = no custom field record exists with this fieldId
						// We need to use isNotFilter to negate the group
						groupBuilder.isNotFilter();
						filterWasAdded = true;
					} else {
						filterWasAdded = parse.dateRange(
							groupBuilder,
							valueField,
							dateValue,
							'YYYY-MM-DD',
							options.useTags,
							false,
							false,
							options.preserveDatePresets
						);
					}
					break;
				}

				case 'Boolean':
				case 'Checkbox':
					var val = filter.value as boolean;
					if (!filter.value) {
						groupBuilder.isNotFilter();
						val = true;
					}
					filterWasAdded = parse.boolean(groupBuilder, valueField, val);
					break;

				case 'Text':
				case 'String':
				case 'Link':
				case 'Email':
				case 'Phone':
					ct = filter.comparisonType ? ComparisonTypes[filter.comparisonType] : ComparisonTypes.WildcardEnd;
					filterWasAdded = parse.string(groupBuilder, valueField, filter.value as string, ct);
					break;

				case 'Select':
				case 'MultiSelect':
				case 'User':
				case 'Users':
					if (Array.isArray(filter.value) && filter.value && filter.value.length !== 0) {
						ct = filter.comparisonType ? ComparisonTypes[filter.comparisonType] : ComparisonTypes.Term;

						if (notCTToCT[ct as keyof typeof notCTToCT]) {
							groupBuilder.isNotFilter();
							ct = notCTToCT[ct as keyof typeof notCTToCT];
						}

						const selfIndex = filter.value.indexOf('self');

						if (selfIndex > -1) {
							const value = [...filter.value];
							value[selfIndex] = `${AppService.getSelf().id}`;

							groupBuilder.addFilter({ field: valueField }, ct, value);
						} else {
							groupBuilder.addFilter({ field: valueField }, ct, filter.value);
						}

						filterWasAdded = true;
					}
					break;

				default:
					if (!isEmptySelection) {
						groupBuilder.addFilter({ field: valueField }, ComparisonTypes.Term, filter.value);
						filterWasAdded = true;
					}
					break;
			}
		} else if (!isEmptySelection) {
			groupBuilder.addFilter({ field: tempPrefix + 'custom.value' }, ComparisonTypes.Term, filter.value);
			filterWasAdded = true;
		}

		// Only set groupFilter if any filter was parsed
		if (filterWasAdded) {
			groupBuilder.addFilter(
				{ field: tempPrefix + 'custom.fieldId' },
				ComparisonTypes.Equals,
				filterConfig.field
			);

			addCustomFieldTypeFilter(groupBuilder, filterConfig, tempPrefix);
			groupBuilder.done();
		}
	}
};

export default buildCustomFilter;

function addCustomFieldTypeFilter(
	groupBuilder: RequestBuilder,
	filterConfig: CustomFieldFilterConfig,
	tempPrefix?: string
) {
	switch (filterConfig.$field?.nameType) {
		case 'Todo':
			groupBuilder.addFilter(
				{ field: (tempPrefix || '') + 'activityType.id' },
				ComparisonTypes.Equals,
				AppService.getTodoTypes().TODO.id
			);
			break;
		case 'Activity':
			groupBuilder.addFilter(
				{ field: (tempPrefix || '') + 'activityType.id' },
				ComparisonTypes.Equals,
				AppService.getTodoTypes().PHONE_CALL.id
			);
			break;
		case 'Appointment':
			groupBuilder.addFilter({ field: (tempPrefix || '') + 'isAppointment' }, ComparisonTypes.Equals, true);
			break;
	}
}
